// Tooltip and Toast.
import { computePosition, autoUpdate, offset, flip, shift, arrow } from "@floating-ui/dom";
import { I80Element, define, h, nextId } from "../base.js";
import { I80Alert } from "./content.js";

const FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]";
const ARROW_SVG = '<svg class="i80-tooltip-pointer" width="16" height="7" viewBox="0 0 16 7" xmlns="http://www.w3.org/2000/svg"><path d="M0 7H16L9.11989 0.444571C8.49776 -0.148191 7.50224 -0.148191 6.88011 0.444572L0 7Z"/></svg>';

/**
 * <i80-tooltip text="Copies the plan with all its tasks">
 *   <i80-button icon="duplicate" icon-only>Duplicate</i80-button>
 * </i80-tooltip>
 * <i80-tooltip text="Shown to everyone on the team">Visibility</i80-tooltip>
 *
 * Wraps a focusable element you write inside, or, for plain text, a
 * button.i80-tooltip-button (as Helios' TooltipButton). Shows on hover and focus,
 * hides on Esc. The box uses the Helios (Tippy) tooltip styles.
 */
export class I80Tooltip extends I80Element {
  static get observedAttributes() {
    return ["text", "placement"];
  }

  setup() {
    let box = this.part("tooltip");
    if (!box) {
      const content = this.takeContent();
      // A trigger another element built (<i80-time> wraps its <time> in one) is used as it is.
      const given = this.querySelector(':scope > [data-i80-part="trigger"]');
      const own = given ?? content.find((n) => n.nodeType === 1 && (n.matches(FOCUSABLE) || n.querySelector?.(FOCUSABLE) || n.localName.startsWith("i80-")));
      if (!own) {
        this.append(h("button", { type: "button", class: "i80-tooltip-button i80-tooltip-button--is-inline", "data-i80-part": "trigger" }, content));
      }
      box = h("div", { class: "tippy-box", "data-theme": "i80", "data-state": "visible", role: "tooltip", popover: "manual", "data-i80-part": "tooltip" },
        h("div", { class: "tippy-content" }),
        h("div", { class: "tippy-svg-arrow" }));
      box.lastChild.innerHTML = ARROW_SVG;
      // Undo the UA popover styles the Tippy theme doesn't set.
      Object.assign(box.style, { margin: "0", border: "0", overflow: "visible", inset: "auto", position: "fixed", maxWidth: "var(--i80-tooltip-max-width, 280px)" });
      this.append(box);
    }
    this._box = box;
    box.id = nextId("i80-tooltip");

    const show = () => this.show();
    const hide = () => this._hideSoon();
    this.addEventListener("pointerenter", show, true);
    this.addEventListener("pointerleave", hide, true);
    this.addEventListener("focusin", show);
    this.addEventListener("focusout", hide);
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && box.matches(":popover-open")) {
        e.stopPropagation();
        this.hide();
      }
    });
  }

  get trigger() {
    return this.part("trigger") ?? [...this.querySelectorAll(FOCUSABLE)].find((el) => !this._box.contains(el)) ?? this.firstElementChild;
  }

  update() {
    this._box.firstChild.textContent = this.getAttribute("text") ?? "";
    // Child elements (e.g. <i80-button>) render their button after this runs.
    queueMicrotask(() => this._describe());
  }

  _describe() {
    const t = this.trigger;
    if (t && t !== this._box) {
      const ids = (t.getAttribute("aria-describedby") ?? "").split(" ").filter((x) => x && !x.startsWith("i80-tooltip-"));
      t.setAttribute("aria-describedby", [...ids, this._box.id].join(" "));
    }
  }

  show() {
    clearTimeout(this._hideTimer);
    const box = this._box;
    const t = this.trigger;
    if (!t || !this.getAttribute("text") || box.matches(":popover-open")) return;
    this._describe();
    box.showPopover();
    const placement = this.choice("placement", ["top", "bottom", "left", "right", "top-start", "top-end", "bottom-start", "bottom-end", "left-start", "left-end", "right-start", "right-end"], "top");
    const arrowEl = box.lastChild;
    this._stop = autoUpdate(t, box, () =>
      computePosition(t, box, {
        placement,
        strategy: "fixed",
        middleware: [offset(10), flip({ padding: 8 }), shift({ padding: 8 }), arrow({ element: arrowEl, padding: 8 })],
      }).then(({ x, y, placement: final, middlewareData }) => {
        Object.assign(box.style, { left: `${x}px`, top: `${y}px` });
        box.setAttribute("data-placement", final);
        const a = middlewareData.arrow ?? {};
        Object.assign(arrowEl.style, { left: a.x != null ? `${a.x}px` : "", top: a.y != null ? `${a.y}px` : "" });
      }),
    );
  }

  hide() {
    clearTimeout(this._hideTimer);
    this._stop?.();
    this._stop = null;
    if (this._box.matches(":popover-open")) this._box.hidePopover();
  }

  // A short grace period lets the pointer move from the trigger onto the tooltip.
  _hideSoon() {
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => {
      if (!this.matches(":hover") && !this.contains(document.activeElement)) this.hide();
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.hide();
  }
}

/**
 * <i80-toast color="success" heading="Plan published" dismissible>Everyone can see it now.</i80-toast>
 * An inline alert with elevation. For toasts from JavaScript (or an HTMX HX-Trigger), use
 * showToast({ heading, text, color, timeout }), which stacks them in the bottom-right corner.
 */
export class I80Toast extends I80Alert {
  update() {
    if (!this.hasAttribute("type")) this.setAttribute("type", "inline");
    super.update();
    this._box.classList.add("i80-toast");
    this._box.setAttribute("role", "alert");
    this._box.setAttribute("aria-live", "polite");
  }
}

export function showToast({ heading = "", text = "", color = "neutral", timeout = 6000, dismissible = true } = {}) {
  let region = document.querySelector("[data-i80-toasts]");
  if (!region) {
    region = h("div", { "data-i80-toasts": "", style: "position:fixed;right:24px;bottom:24px;z-index:60;display:flex;flex-direction:column;align-items:flex-end;gap:12px" });
    document.body.append(region);
  }
  const toast = h("i80-toast", { color, heading: heading || null, dismissible }, text);
  region.append(toast);
  if (timeout) setTimeout(() => toast.remove(), timeout);
  return toast;
}

define("i80-tooltip", I80Tooltip);
define("i80-toast", I80Toast);

// HTMX: HX-Trigger: {"i80-toast": {"heading": "Saved", "color": "success"}}
document.addEventListener("i80-toast", (e) => {
  if (e.detail && typeof e.detail === "object") showToast(e.detail.value ?? e.detail);
});
